/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  const html = document.documentElement;
  const AirX = window.AirX || {};
  const routes = window.AirXRoutes || {};
  const RECENT_KEY = 'airx:recent-searches';
  const RECENT_MAX = 6;

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

  const sizedImage = (url, width) => {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  };

  const arrowSvg =
    '<svg class="icon icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 12h16m0 0-6-6m6 6-6 6"/></svg>';

  const readRecent = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(stored) ? stored.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-results]');
      this.recentWrap = this.querySelector('[data-recent]');
      this.recentList = this.querySelector('[data-recent-list]');
      this.status = this.querySelector('[data-search-status]');
      this.abortController = null;

      try {
        this.strings = JSON.parse(this.querySelector('[data-search-strings]').textContent);
      } catch (e) {
        this.strings = {};
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });
      this.querySelectorAll('[data-search-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      const handleInput = () => this.onInput();
      this.input.addEventListener('input', AirX.debounce ? AirX.debounce(handleInput, 250) : handleInput);

      this.form.addEventListener('submit', () => this.saveRecent(this.input.value));
      this.results.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.saveRecent(this.input.value);
      });

      const clearBtn = this.querySelector('[data-clear-recent]');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
          this.renderRecent();
          this.input.focus();
        });
      }

      this.recentList.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-recent-term]');
        if (!chip) return;
        this.input.value = chip.getAttribute('data-recent-term');
        this.input.focus();
        this.onInput();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || null;

      const menuDrawer = document.getElementById('MenuDrawer');
      if (menuDrawer && typeof menuDrawer.close === 'function') menuDrawer.close();

      this.hidden = false;
      window.requestAnimationFrame(() => this.classList.add('is-open'));
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      if (AirX.overlayOpened) AirX.overlayOpened('PredictiveSearch');
      if (AirX.trapFocus) AirX.trapFocus(this, this.input);
      else this.input.focus();

      this.renderRecent();

      if (window.gsap && html.classList.contains('motion')) {
        window.gsap.fromTo(
          this.querySelector('.predictive-search__inner'),
          { opacity: 0, y: 36 },
          { opacity: 1, y: 0, duration: 0.65, ease: 'expo.out', clearProps: 'all' }
        );
      }
      if (AirX.emit) AirX.emit('search:open', {});
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      if (AirX.overlayClosed) AirX.overlayClosed('PredictiveSearch');
      if (AirX.releaseFocus) AirX.releaseFocus();
      window.setTimeout(() => {
        this.hidden = true;
      }, 350);
      if (AirX.emit) AirX.emit('search:close', {});
    }

    onInput() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        if (this.abortController) this.abortController.abort();
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.setStatus('');
        this.renderRecent();
        return;
      }
      this.recentWrap.hidden = true;
      this.fetchResults(query);
    }

    fetchResults(query) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setStatus(this.strings.loading || '');
      this.results.classList.add('is-loading');

      const base = routes.predictive_search_url || '/search/suggest';
      const url =
        base +
        '.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=8' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: this.abortController.signal })
        .then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.json();
        })
        .then((data) => {
          const results = (data && data.resources && data.resources.results) || {};
          this.render(query, results);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.results.classList.remove('is-loading');
          this.setStatus('');
        });
    }

    render(query, results) {
      const s = this.strings;
      const products = results.products || [];
      const collections = results.collections || [];
      const pages = results.pages || [];
      const articles = results.articles || [];
      const total = products.length + collections.length + pages.length + articles.length;
      const q = escapeHtml(query);
      let markup = '';

      if (products.length) {
        markup +=
          '<section class="predictive-search__group" aria-label="' + escapeHtml(s.products || '') + '">' +
          '<h3 class="predictive-search__group-title">' + escapeHtml(s.products || '') + '</h3>' +
          '<ul class="predictive-search__products" role="list">' +
          products
            .map((p) => {
              const image = p.image || (p.featured_image && p.featured_image.url) || '';
              return (
                '<li class="predictive-search__product" data-ps-item>' +
                '<a class="predictive-search__product-link" href="' + escapeHtml(p.url) + '">' +
                '<span class="predictive-search__thumb">' +
                (image ? '<img src="' + escapeHtml(sizedImage(image, 360)) + '" alt="" width="180" height="225" loading="lazy">' : '') +
                '</span>' +
                '<span class="predictive-search__product-title">' + escapeHtml(p.title) + '</span>' +
                '<span class="predictive-search__product-price">' + this.formatPrice(p.price) + '</span>' +
                '</a></li>'
              );
            })
            .join('') +
          '</ul></section>';
      }

      const groups = [
        { items: collections, label: s.collections },
        { items: pages, label: s.pages },
        { items: articles, label: s.articles },
      ].filter((g) => g.items.length);

      if (groups.length) {
        markup +=
          '<div class="predictive-search__link-groups">' +
          groups
            .map(
              (g) =>
                '<section class="predictive-search__group" data-ps-item aria-label="' + escapeHtml(g.label || '') + '">' +
                '<h3 class="predictive-search__group-title">' + escapeHtml(g.label || '') + '</h3>' +
                '<ul class="predictive-search__links" role="list">' +
                g.items
                  .map(
                    (item) =>
                      '<li><a class="predictive-search__link" href="' + escapeHtml(item.url) + '">' +
                      escapeHtml(item.title) +
                      '</a></li>'
                  )
                  .join('') +
                '</ul></section>'
            )
            .join('') +
          '</div>';
      }

      if (!total) {
        markup += '<p class="predictive-search__empty">' + (s.noResults || '').replace('__TERMS__', q) + '</p>';
      }

      markup +=
        '<div class="predictive-search__cta" data-ps-item>' +
        '<a class="btn btn--ghost" href="' + escapeHtml(routes.search_url || '/search') + '?q=' + encodeURIComponent(query) + '">' +
        (s.searchFor || 'Search for “__TERMS__”').replace('__TERMS__', q) +
        '<span class="btn__arrow">' + arrowSvg + '</span>' +
        '</a></div>';

      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.results.classList.remove('is-loading');
      this.setStatus((s.resultsCount || '__COUNT__').replace('__COUNT__', String(total)));

      if (window.gsap && html.classList.contains('motion')) {
        const items = this.results.querySelectorAll('[data-ps-item]');
        if (items.length) {
          window.gsap.fromTo(
            items,
            { opacity: 0, y: 14 },
            { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.035, clearProps: 'all' }
          );
        }
      }
    }

    formatPrice(price) {
      const value = parseFloat(price);
      if (isNaN(value)) return '';
      if (AirX.formatMoney) return AirX.formatMoney(Math.round(value * 100));
      return '$' + value.toFixed(2);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text || '';
    }

    renderRecent() {
      const terms = readRecent();
      if (!terms.length || this.input.value.trim().length >= 2) {
        this.recentWrap.hidden = true;
        return;
      }
      this.recentList.innerHTML = terms
        .map(
          (term) =>
            '<li><button type="button" class="predictive-search__recent-chip" data-recent-term="' +
            escapeHtml(term) + '">' + escapeHtml(term) + '</button></li>'
        )
        .join('');
      this.recentWrap.hidden = false;
    }

    saveRecent(query) {
      const term = (query || '').trim();
      if (term.length < 2) return;
      const next = [term].concat(
        readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())
      ).slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (e) {}
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-search-open]');
    if (!opener) return;
    const overlay = document.getElementById('PredictiveSearch');
    if (overlay && typeof overlay.open === 'function') {
      e.preventDefault();
      overlay.open(opener);
    }
  });
})();
